'use client';

import { SCENE_NAME_EXT } from '@gnsx/genesys.js';
import { useEffect, useState } from 'react';

import { editorStore } from '@/app/editor/store/editorStore';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { PROJECT_ASSETS_FOLDER } from '@/const';
import { AssetType } from '@/editor/core/assetTypes';
import { makeUniqueName } from '@/editor/core/nameHelpers';
import { cn } from '@/lib/utils';

// Regex for valid scene name (alphanumeric, underscore, hyphen only, and slash is allowed but not ending with slash)
const VALID_SCENE_NAME_REGEX = /^[a-zA-Z0-9_-]+(\/[a-zA-Z0-9_-]+)*$/;

const validateSceneName = (name: string): string | null => {
  if (!name.trim()) {
    return 'Scene name cannot be empty';
  }
  if (!VALID_SCENE_NAME_REGEX.test(name)) {
    return 'Scene name can only contain letters, numbers, underscores, and hyphens';
  }
  return null;
};

/** Options for setting the new scene as default for different modes */
export interface NewSceneOptions {
  setAsDefaultGame: boolean;
  setAsDefaultServer: boolean;
}

interface SaveSceneDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (sceneName: string, options?: NewSceneOptions) => Promise<void>;
  mode?: 'new' | 'saveAs';
}

export const SaveSceneDialog = ({ isOpen, onOpenChange, onSave, mode = 'new' }: SaveSceneDialogProps) => {
  const [tempSceneName, setTempSceneName] = useState<string>('');
  const [inputError, setInputError] = useState<string>('');
  const [existingSceneNames, setExistingSceneNames] = useState<string[]>([]);

  // Checkbox states for default map options (checked by default)
  const [setAsDefaultGame, setSetAsDefaultGame] = useState<boolean>(true);
  const [setAsDefaultServer, setSetAsDefaultServer] = useState<boolean>(true);

  // Load existing scene names when dialog opens
  useEffect(() => {
    if (isOpen) {
      const sceneFiles = editorStore.assets.assetTree.getAssetFiles(AssetType.Scene);
      const sceneNames = sceneFiles.map(file => {
        // Get the scene name without the extension and without the assets folder prefix
        const relativePath = file.path.replace(PROJECT_ASSETS_FOLDER + '/', '');
        return relativePath.replace(SCENE_NAME_EXT, '');
      });
      setExistingSceneNames(sceneNames);
    }
  }, [isOpen]);

  const handleNameInput = (value: string) => {
    setTempSceneName(value);
    const error = validateSceneName(value);
    setInputError(error ?? '');
  };

  const handleSubmit = async () => {
    const error = validateSceneName(tempSceneName);
    if (error) {
      setInputError(error);
      return;
    }

    // Auto-generate unique name if there's a conflict
    const uniqueName = makeUniqueName(tempSceneName, existingSceneNames);

    // Build options only for new scene mode
    const options: NewSceneOptions | undefined = mode === 'new' ? {
      setAsDefaultGame,
      setAsDefaultServer,
    } : undefined;

    onOpenChange(false);
    await onSave(uniqueName, options);
    // Clear the input after successful save
    setTempSceneName('');
  };

  // Reset state when dialog opens
  const handleOpenChange = (open: boolean) => {
    if (open) {
      setTempSceneName('');
      setInputError('');
      // Reset checkboxes to true by default for new scene mode
      setSetAsDefaultGame(true);
      setSetAsDefaultServer(true);
    }
    onOpenChange(open);
  };

  // Check if the name will be auto-renamed
  const willBeRenamed = tempSceneName.trim() && !inputError &&
    existingSceneNames.some(existing => existing.toLowerCase() === tempSceneName.toLowerCase());
  const uniqueName = willBeRenamed ? makeUniqueName(tempSceneName, existingSceneNames) : tempSceneName;

  // Only show checkboxes in new scene mode
  const showCheckboxes = mode === 'new';

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Enter Scene Name</DialogTitle>
        </DialogHeader>
        <div className="py-4">
          <Input
            placeholder="Enter scene name (letters, numbers, underscores, hyphens)"
            value={tempSceneName}
            onChange={(e) => handleNameInput(e.target.value)}
            onKeyDown={(e) => {
              e.stopPropagation();
              e.key === 'Enter' && !inputError && tempSceneName.trim() && handleSubmit();
            }}
            className={cn(inputError ? 'border-destructive' : '', 'text-sm')}
          />
          {inputError && (
            <p className="text-sm text-destructive font-medium m-2">{inputError}</p>
          )}
          {willBeRenamed && (
            <p className="text-sm text-warning font-medium m-2">
              A scene with this name already exists. It will be saved as: <span className="font-medium">{uniqueName}</span>
            </p>
          )}
          {showCheckboxes && (
            <div className="flex flex-col gap-3 mt-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  size="sm"
                  id="set-as-default-game"
                  checked={setAsDefaultGame}
                  onCheckedChange={(checked) => setSetAsDefaultGame(checked === true)}
                />
                <Tooltip delayDuration={500}>
                  <TooltipTrigger asChild>
                    <label
                      htmlFor="set-as-default-game"
                      className="text-2xs text-muted-foreground leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-help"
                    >
                      Game Default Scene
                    </label>
                  </TooltipTrigger>
                  <TooltipContent>
                    <span>Set as the default scene when running the game</span>
                  </TooltipContent>
                </Tooltip>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  size="sm"
                  id="set-as-default-server"
                  checked={setAsDefaultServer}
                  onCheckedChange={(checked) => setSetAsDefaultServer(checked === true)}
                />
                <Tooltip delayDuration={500}>
                  <TooltipTrigger asChild>
                    <label
                      htmlFor="set-as-default-server"
                      className="text-2xs text-muted-foreground leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-help"
                    >
                      Server Default Scene
                    </label>
                  </TooltipTrigger>
                  <TooltipContent>
                    <span>Set as the default scene for the dedicated server</span>
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button onClick={() => handleOpenChange(false)}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            disabled={!!inputError || !tempSceneName.trim()}
          >
            {mode === 'new' ? 'Create Scene' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

SaveSceneDialog.displayName = 'SaveSceneDialog';
